/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { EAS_OSD_PRIORITY } from "../../config";
import type { UnityAppDispatch } from "../UnityAppStore";
import { showOsd } from "./osdSlice";

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Required by CodingGuidelines §7 (D-09)
const TAG: string = "osdEasThunks";

/** Params for `showEasOsd`. */
export interface ShowEasOsdParams {
  /** Alert body text. */
  message: string;
  /** Message/error ID shown in the OSD's top-right corner. */
  id: string;
  /**
   * `easConfiguration.easDisplayMode` value (e.g. "us", "canada") that
   * selects the EAS OSD template — see `EasOsdTemplateSelector`.
   */
  country: string;
  /**
   * Whether the alert carries video (`audioVideo` or `videoOnly`). Clears the
   * alert backdrop and gives the standalone media element a surface, so the
   * picture is visible behind the message rather than painted over.
   */
  hasVideo: boolean;
  /**
   * Whether the viewer may dismiss this alert, as resolved by
   * `EasDismissPolicy` — the market's template and the alert's own
   * `allowUserExit` together. Drives the "Press BACK to dismiss" footer, and
   * is the same value `EasInputGate` gates the dismiss key on.
   */
  isDismissible: boolean;
}

/**
 * showEasOsd — the single entry point for putting an Emergency Alert System
 * (EAS) alert on the shared `osd` slice.
 *
 * Always dispatches at `EAS_OSD_PRIORITY`, the highest priority on the shared
 * ladder, so an EAS alert can never be pre-empted by, or fail to pre-empt,
 * any other OSD/dialog request. Callers never dispatch `showOsd` directly for
 * an EAS alert — this hardcodes the one priority value so a future producer
 * cannot under- or over-rank it by mistake.
 *
 * Reuses the same `osd` slice, `OsdContainer` mount point, and priority
 * arbitration as every other OSD request; `OsdContainer` renders the
 * country-specific `EasOsd` template instead of the generic `Osd` component
 * whenever `easCountry` is set.
 */
export const showEasOsd =
  (params: ShowEasOsdParams) =>
  (dispatch: UnityAppDispatch): void => {
    dispatch(
      showOsd({
        message: params.message,
        code: params.id,
        priority: EAS_OSD_PRIORITY,
        isRepositionRequired: false,
        easCountry: params.country,
        easHasVideo: params.hasVideo,
        easIsDismissible: params.isDismissible
      })
    );
  };
